import { useRef, useState } from 'react';
import type { Address, Coordinates } from '../types';
import { isFranceCountry } from '../utils/countries';

export type BanTriggerField = 'city' | 'zip_code' | 'administrative_region_1' | 'administrative_region_2';

export interface BanSuggestion {
  display: string;
  city?: string;
  zip_code?: string;
  administrative_region_1: string;
  administrative_region_2: string;
  // Commune centroid — only set for city/zip_code suggestions (a department or region has no pin)
  latitude?: number;
  longitude?: number;
}

export interface BanFeatureProperties {
  name: string;
  city?: string;
  postcode: string;
  context: string; // "dept_code, DeptName, RegionName" (overseas communes have only two parts)
  citycode?: string; // INSEE code — the stable per-commune identifier
  depcode?: string;
}

export interface BanFeature {
  properties: BanFeatureProperties;
  // GeoJSON order: [longitude, latitude]
  geometry?: { coordinates: [number, number] };
}

export function parseContext(context: string): { deptName: string; regionName: string } {
  const parts = context.split(', ');
  return { deptName: parts[1] ?? '', regionName: parts[2] ?? '' };
}

/**
 * Query the BAN (Base Adresse Nationale) municipality endpoint.
 * Shared by the autocomplete below and by the background geocoder in utils/banGeocode.ts.
 */
export async function fetchBanMunicipalities(
  query: string,
  options: { limit?: number; autocomplete?: boolean; postcode?: string; signal?: AbortSignal } = {}
): Promise<BanFeature[]> {
  const { limit = 6, autocomplete = true, postcode, signal } = options;
  const params = new URLSearchParams({
    q: query,
    type: 'municipality',
    limit: String(limit),
    autocomplete: autocomplete ? '1' : '0',
  });
  if (postcode) params.set('postcode', postcode);

  const resp = await fetch(`https://api-adresse.data.gouv.fr/search/?${params.toString()}`, { signal });
  if (!resp.ok) return [];
  const data: { features?: BanFeature[] } = await resp.json();
  return data.features ?? [];
}

function coordsOf(feature: BanFeature): Coordinates | undefined {
  const coords = feature.geometry?.coordinates;
  if (!coords || coords.length < 2) return undefined;
  const [longitude, latitude] = coords;
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return undefined;
  return { latitude, longitude };
}

async function fetchBanSuggestions(query: string, triggerField: BanTriggerField): Promise<BanSuggestion[]> {
  const features = await fetchBanMunicipalities(query);

  if (triggerField === 'administrative_region_2') {
    const seen = new Set<string>();
    const results: BanSuggestion[] = [];
    for (const f of features) {
      const { regionName } = parseContext(f.properties.context);
      if (regionName && !seen.has(regionName)) {
        seen.add(regionName);
        results.push({ display: regionName, administrative_region_1: '', administrative_region_2: regionName });
      }
    }
    return results;
  }

  if (triggerField === 'administrative_region_1') {
    const seen = new Set<string>();
    const results: BanSuggestion[] = [];
    for (const f of features) {
      const { deptName, regionName } = parseContext(f.properties.context);
      if (deptName && !seen.has(deptName)) {
        seen.add(deptName);
        results.push({ display: `${deptName} — ${regionName}`, administrative_region_1: deptName, administrative_region_2: regionName });
      }
    }
    return results;
  }

  // city or zip_code: full municipality results, with the commune centroid
  return features.map(f => {
    const cityName = f.properties.city || f.properties.name;
    const { deptName, regionName } = parseContext(f.properties.context);
    return {
      display: `${cityName} (${f.properties.postcode}) — ${deptName}, ${regionName}`,
      city: cityName,
      zip_code: f.properties.postcode,
      administrative_region_1: deptName,
      administrative_region_2: regionName,
      ...coordsOf(f),
    };
  });
}

/**
 * @param onSelect - Receives the address fields to apply, plus the coordinates to store:
 *   the commune centroid for a city/zip pick, or `null` for a department/region pick. `null`
 *   clears rather than keeps: picking a new department while the city text stays put would
 *   otherwise leave the pin on the previous department's identically-named commune.
 */
export function useBanAutocomplete(
  onSelect: (updates: Partial<Address>, coordinates: Coordinates | null) => void
) {
  const [suggestions, setSuggestions] = useState<BanSuggestion[]>([]);
  const [activeField, setActiveField] = useState<BanTriggerField | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * @param country - The address's current country. The BAN is the *French* address database,
   *   so a venue in Berlin must not be offered French communes: anything but France (or a
   *   country left blank) suppresses the lookup entirely rather than returning nothing, so no
   *   request is made and no dropdown appears. See `isFranceCountry`.
   */
  function triggerSearch(field: BanTriggerField, value: string, country?: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!isFranceCountry(country)) {
      setSuggestions([]);
      setActiveField(null);
      return;
    }

    if (value.length < 2) {
      setSuggestions([]);
      setActiveField(null);
      return;
    }

    setActiveField(field);
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const results = await fetchBanSuggestions(value, field);
        setSuggestions(results);
      } catch {
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  }

  function selectSuggestion(suggestion: BanSuggestion) {
    const updates: Partial<Address> = { country: 'France' };
    let coordinates: Coordinates | null = null;

    if (activeField === 'city' || activeField === 'zip_code') {
      if (suggestion.city !== undefined) updates.city = suggestion.city;
      if (suggestion.zip_code !== undefined) updates.zip_code = suggestion.zip_code;
      if (suggestion.administrative_region_1) updates.administrative_region_1 = suggestion.administrative_region_1;
      if (suggestion.administrative_region_2) updates.administrative_region_2 = suggestion.administrative_region_2;
      if (suggestion.latitude !== undefined && suggestion.longitude !== undefined) {
        coordinates = { latitude: suggestion.latitude, longitude: suggestion.longitude };
      }
    } else if (activeField === 'administrative_region_1') {
      if (suggestion.administrative_region_1) updates.administrative_region_1 = suggestion.administrative_region_1;
      if (suggestion.administrative_region_2) updates.administrative_region_2 = suggestion.administrative_region_2;
    } else if (activeField === 'administrative_region_2') {
      if (suggestion.administrative_region_2) updates.administrative_region_2 = suggestion.administrative_region_2;
    }

    onSelect(updates, coordinates);
    dismiss();
  }

  function dismiss() {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSuggestions([]);
    setActiveField(null);
  }

  return { suggestions, activeField, isLoading, triggerSearch, selectSuggestion, dismiss };
}
